import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getProduct, removeProduct, updateProduct } from 'slice/productSlice';
import AdminTable from 'common/AdminTable';
import TruncatedMessage from 'common/TruncatedMessage';

const ProductList = () => {
  const dispatch = useDispatch();
  const cartData = useSelector((state) => state.product);

  const [showModal, setShowModal] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [imageFiles, setImageFiles] = useState([]); // newly selected files

  useEffect(() => {
    dispatch(getProduct());
  }, [dispatch]);

const formatImages = (images) => {
  if (!images) return [];
  if (Array.isArray(images)) return images;
  if (typeof images === 'string') {
    const trimmed = images.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
    } catch (error) {
      console.warn("Not a JSON string, fallback to splitting:", error);
    }
    return trimmed.split(',').map((img) => img.trim()).filter(Boolean);
  }
  return [];
};

  const getProductImages = (product) => {
    const primary = formatImages(product?.images);
    if (primary.length) return primary;
    return formatImages(product?.image);
  };


  const handleEditClick = (product) => {
    setCurrentProduct({
      ...product,
      images: getProductImages(product),
    });
    setImageFiles([]);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentProduct(null);
    setImageFiles([]);
  };

  const handleSave = () => {
    const formData = new FormData();
    formData.append('productkey', currentProduct.productkey || '');
    formData.append('name', currentProduct.name);
    formData.append('sprice', currentProduct.sprice);
    formData.append('quantity', currentProduct.quantity);
    formData.append('plan_duration', currentProduct.plan_duration || '');
    formData.append('long_description', currentProduct.long_description);

    imageFiles.forEach((file) => {
      formData.append('images', file);
    });

    dispatch(updateProduct({ id: currentProduct.id, data: formData }));
    handleCloseModal();
  };

  const handleImageChange = (e) => {
    setImageFiles(Array.from(e.target.files));
  };

  const getImageUrl = (img) => {
    const base = process.env.REACT_APP_BACKEND.endsWith('/')
      ? process.env.REACT_APP_BACKEND
      : process.env.REACT_APP_BACKEND + '/';
    return `${base}uploads/${encodeURIComponent(img)}`;
  };

  const tableHeaders = [
    { Header: 'ID', accessor: 'id' },
    { Header: 'Product Key', accessor: 'productkey' },
    {
      Header: 'Images',
      accessor: 'images',
      Cell: ({ row }) => {
        const images = getProductImages(row.original);
        return (
          <>
            {images.map((img, index) => (
              <img
                key={index}
                src={getImageUrl(img)}
                alt={row.original.name || 'product'}
                width={50}
                className="mr-1"
              />
            ))}
          </>
        );
      },
    },
    { Header: 'Name', accessor: 'name' },
    { Header: 'Price', accessor: 'sprice' },
    { Header: 'Quantity', accessor: 'quantity' },
    { Header: 'Plan Duration', accessor: 'plan_duration' },
    {
      Header: 'Description',
      accessor: 'long_description',
      Cell: ({ row }) => <TruncatedMessage text={row.original.long_description} maxLength={30} />,
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          <button className="btn removebtn mx-1 mb-2" onClick={() => dispatch(removeProduct(row.original.id))}>
            Remove
          </button>
          <button className="btn editbtn mx-1" onClick={() => handleEditClick(row.original)}>
            Edit
          </button>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="container-fluid mt-5">
        <div className="mainheadig mx-4">
          <h4 className="text-white font-weight-bold">Medicine List</h4>
        </div>
        {cartData?.data?.length > 0 && <AdminTable tableHeaders={tableHeaders} tableData={cartData.data} />}
      </div>

      {showModal && currentProduct && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Product</h5>
                <button type="button" className="close" onClick={handleCloseModal}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <form>
                  <div className="form-group">
                    <label htmlFor="productkey">Product Key</label>
                    <input
                      type="text"
                      className="form-control"
                      value={currentProduct.productkey || ''}
                      id="productkey"
                      onChange={(e) => setCurrentProduct({ ...currentProduct, productkey: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={currentProduct.name}
                      id="name"
                      onChange={(e) => setCurrentProduct({ ...currentProduct, name: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="price">Price</label>
                    <input
                      type="number"
                      className="form-control"
                      id="price"
                      value={currentProduct.sprice}
                      onChange={(e) => setCurrentProduct({ ...currentProduct, sprice: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="quantity">Quantity</label>
                    <input
                      type="number"
                      className="form-control"
                      id="quantity"
                      value={currentProduct.quantity}
                      onChange={(e) => setCurrentProduct({ ...currentProduct, quantity: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="planDuration">Plan Duration</label>
                    <input
                      type="text"
                      className="form-control"
                      id="planDuration"
                      value={currentProduct.plan_duration || ''}
                      onChange={(e) => setCurrentProduct({ ...currentProduct, plan_duration: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="Description">Description</label>
                    <textarea
                      className="form-control"
                      id="Description"
                      value={currentProduct.long_description}
                      onChange={(e) => setCurrentProduct({ ...currentProduct, long_description: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="Images">Images</label>
                    <input type="file" className="form-control" multiple onChange={handleImageChange} id="Images" />
                    <div className="mt-2">
                      {imageFiles.length > 0
                        ? imageFiles.map((file, index) => (
                            <img key={index} src={URL.createObjectURL(file)} alt="new" width={50} className="mr-1" />
                          ))
                        : currentProduct.images?.map((img, index) => (
                            <img key={index} src={getImageUrl(img)} alt={currentProduct.name || 'product'} width={50} className="mr-1" />
                          ))}
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Close
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSave}>
                  Save changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductList;
